/**
 * Backend API Configuration
 *
 * Manages different backend environments for local development.
 * Use environment variable REACT_APP_BACKEND_ENV to switch between:
 * - 'mock': Uses .proxyrc.cjs with mock responses (default)
 * - 'local-docker': Proxies to local Docker container
 * - 'staging': Points to staging environment (future)
 */

export type BackendEnvironment = 'mock' | 'local-docker' | 'staging';

export interface BackendConfig {
  baseURL: string;
  timeout?: number;
  withCredentials?: boolean;
}

const backendConfigs: Record<BackendEnvironment, BackendConfig> = {
  mock: {
    baseURL: '', // Empty string uses local proxy
    timeout: 10000,
  },
  'local-docker': {
    baseURL: '', // Empty string - proxy will handle forwarding
    timeout: 10000,
    withCredentials: false,
  },
  staging: {
    baseURL: '', // TODO: Add staging URL when available
    timeout: 10000,
    withCredentials: true,
  },
};

/**
 * Get the current backend environment from environment variables
 */
export function getBackendEnvironment(): BackendEnvironment {
  const backendEnv = process.env.REACT_APP_BACKEND_ENV;

  if (backendEnv && backendEnv in backendConfigs) {
    return backendEnv as BackendEnvironment;
  }

  // Default to mock for local development
  return 'mock';
}

/**
 * Get the backend configuration for the current environment
 */
export function getBackendConfig(): BackendConfig {
  const env = getBackendEnvironment();
  const config = backendConfigs[env];

  if (typeof window !== 'undefined') {
    console.log(`🔧 Backend Environment: ${env}`);
    console.log(`🔧 Backend URL: ${config.baseURL || 'Using local proxy'}`);
  }

  return config;
}
